import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { fetchWordSearch, finishGame } from '../../API';
import { buildWordSearchGrid } from './wordSearchBuilder';
import WordSearchView from './WordSearchView';

export default function WordSearchGame() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const loaded = useRef(false);

    const [gameData, setGameData] = useState(null);
    const [error, setError]       = useState(false);
    const [gameId, setGameId]     = useState(null);

    const handleWin = async () => {
        let earnedPoints = 0;
        if (gameId) {
            try {
                const result = await finishGame(gameId);
                earnedPoints = result.earned ?? 0;
            } catch (err) {
                console.warn('Nie udało się zapisać wyniku:', err.message);
            }
        }

        setTimeout(() => {
            Swal.fire({
                title: 'Brawo!',
                html: `
                    <div style="font-size: 60px; margin: 10px 0;">🔍</div>
                    <div style="font-size: 18px; font-weight: 500; color: #334155;">
                        Znalazłeś wszystkie słowa!
                    </div>
                    ${earnedPoints > 0 ? `
                    <div style="margin-top: 12px; font-size: 22px; font-weight: 800; color: #2563eb;">
                        +${earnedPoints} pkt 🏆
                    </div>` : ''}
                `,
                confirmButtonText: 'Powróć do menu',
                confirmButtonColor: '#2563eb',
                allowOutsideClick: false,
                allowEscapeKey: false,
                backdrop: 'rgba(15, 23, 42, 0.8)'
            }).then(r => { if (r.isConfirmed) navigate('/'); });
        }, 150);
    };

    useEffect(() => {
        if (loaded.current) return;
        loaded.current = true;

        const load = async () => {
            try {
                const count      = searchParams.get('count') || '10';
                const seed       = searchParams.get('seed');
                const difficulty = searchParams.get('difficulty') || 'EASY';

                const { gameId: id, words } = await fetchWordSearch(count, seed, difficulty);
                setGameId(id);

                const { grid, rows, cols } = buildWordSearchGrid(words, seed || id);
                setGameData({ grid, words, rows, cols, seed, difficulty });

            } catch (err) {
                console.error('Błąd ładowania wykreślanki:', err);
                setError(true);
            }
        };

        load();
    }, [searchParams]);

    if (error) return (
        <div style={styles.pageWrapper}>
            <div style={styles.card}>
                <div style={styles.center}>
                    <div style={{ fontSize: '40px' }}>🔍</div>
                    <p style={{ color: '#ef4444', fontWeight: '600', margin: '16px 0' }}>
                        Nie udało się wygenerować wykreślanki.
                    </p>
                    <button style={styles.primaryBtn} onClick={() => navigate('/')}>
                        Wróć do menu
                    </button>
                </div>
            </div>
        </div>
    );

    if (!gameData) return (
        <div style={styles.pageWrapper}>
            <div style={styles.card}>
                <div style={styles.center}>
                    <div style={{ fontSize: '28px', marginBottom: '10px' }}>⏳</div>
                    <p style={{ color: '#64748b', fontWeight: '500' }}>
                        Generowanie wykreślanki...
                    </p>
                </div>
            </div>
        </div>
    );

    return (
        <div style={styles.pageWrapper}>
            <div style={styles.card}>

                <div style={styles.header}>
                    <div>
                        <h2 style={styles.title}>Wykreślanka 🔍</h2>
                        <p style={styles.subtitle}>
                            <strong>Słowa:</strong> {gameData.words.length} •{' '}
                            <strong>Poziom:</strong> {gameData.difficulty} •{' '}
                            {gameData.seed && <><strong>Seed:</strong> {gameData.seed}</>}
                        </p>
                    </div>
                    <button
                        style={styles.secondaryBtn}
                        onClick={() => navigate('/')}
                        onMouseOver={e => e.currentTarget.style.backgroundColor = '#cbd5e1'}
                        onMouseOut={e  => e.currentTarget.style.backgroundColor = '#e2e8f0'}
                    >
                        ← Wróć do menu
                    </button>
                </div>

                <WordSearchView
                    grid={gameData.grid}
                    words={gameData.words}
                    onWin={handleWin}
                />

            </div>
        </div>
    );
}

const styles = {
    pageWrapper: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '40px 20px',
        background: 'linear-gradient(135deg, #a4a5ff 0%, #8cb8ff 100%)',
        fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        boxSizing: 'border-box',
    },
    card: {
        backgroundColor: '#fff',
        padding: '30px',
        borderRadius: '20px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
        width: '100%',
        maxWidth: '85vw',
        boxSizing: 'border-box',
    },
    header: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: '2px solid #f1f5f9', paddingBottom: '20px', marginBottom: '30px',
        flexWrap: 'wrap', gap: '15px',
    },
    title:    { margin: 0, fontSize: '24px', color: '#0f172a', fontWeight: 'bold' },
    subtitle: { margin: '4px 0 0', fontSize: '15px', color: '#64748b' },
    primaryBtn: {
        padding: '12px 24px', fontSize: '16px', fontWeight: 'bold',
        backgroundColor: '#2563eb', color: 'white', border: 'none',
        borderRadius: '10px', cursor: 'pointer',
    },
    secondaryBtn: {
        padding: '10px 16px', fontSize: '14px', fontWeight: 'bold',
        backgroundColor: '#e2e8f0', color: '#475569', border: 'none',
        borderRadius: '8px', cursor: 'pointer', transition: 'background-color 0.2s',
    },
    center: {
        textAlign: 'center', padding: '60px 20px',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
    },
};
